import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Check, X, Pencil, Send, Loader2, ShieldAlert, Trash2, CalendarPlus, Pause, Play, SkipForward, History, Plus } from "lucide-react";

const badge = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700", published: "bg-emerald-100 text-emerald-800", locked: "bg-slate-200 text-slate-700" };
const TABS = [["bank", "Topic bank"], ["comments", "Comments"], ["reports", "Reports"], ["controls", "Publishing"]];

export default function BoardAdmin() {
  const [authorized, setAuthorized] = useState(null);
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("bank");
  const [bankStatus, setBankStatus] = useState("pending");
  const [bank, setBank] = useState(null);
  const [reports, setReports] = useState(null);
  const [comments, setComments] = useState(null);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backfillDays, setBackfillDays] = useState(7);

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/discussions/admin/status"); setStatus(data); setAuthorized(true); }
    catch (err) { if ([401, 403].includes(err.response?.status)) setAuthorized(false); else { setAuthorized(false); toast.error(formatApiError(err.response?.data?.detail)); } }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const loadBank = useCallback(async () => {
    if (authorized !== true || tab !== "bank") return;
    setBank(null);
    try { const { data } = await api.get("/discussions/admin/bank", { params: { status: bankStatus, limit: 50 } }); setBank(data.items || data.topics || data.bank || []); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBank([]); }
  }, [authorized, tab, bankStatus]);
  useEffect(() => { loadBank(); }, [loadBank]);

  const loadReports = useCallback(async () => {
    if (authorized !== true || tab !== "reports") return;
    setReports(null);
    try { const { data } = await api.get("/discussions/admin/reports", { params: { status: "open" } }); setReports(data.items || data.reports || []); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setReports([]); }
  }, [authorized, tab]);
  useEffect(() => { loadReports(); }, [loadReports]);

  const loadComments = useCallback(async () => {
    if (authorized !== true || tab !== "comments") return;
    setComments(null);
    try { const { data } = await api.get("/discussions/admin/comments", { params: { status: "hold", limit: 50 } }); setComments(data.items || data.comments || []); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setComments([]); }
  }, [authorized, tab]);
  useEffect(() => { loadComments(); }, [loadComments]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const res = await fn(); toast.success(res?.data?.message || okMsg); return res; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const review = (t, action) => act(() => api.post("/discussions/admin/bank/review", { id: t.id, action }), `Topic ${action}d`).then(loadBank);
  const saveEdit = () => act(() => api.post("/discussions/admin/bank/edit", { id: edit.id, question: edit.question, category: edit.category, intro: edit.intro }), "Topic updated").then(() => { setEdit(null); loadBank(); });
  const createTopic = () => act(() => api.post("/discussions/admin/bank/create", { question: creating.question, category: creating.category, intro: creating.intro }), "Topic created").then(() => { setCreating(null); loadBank(); });
  const schedule = (t, date) => act(() => api.post("/discussions/admin/schedule", { id: t.id, date }), "Scheduled");
  const publishNow = (t) => act(() => api.post("/discussions/admin/publish-now", { id: t.id }), "Published").then(loadStatus);
  const pause = (paused) => act(() => api.post("/discussions/admin/pause", { paused }), paused ? "Publishing paused" : "Publishing resumed").then(loadStatus);
  const skip = () => act(() => api.post("/discussions/admin/skip", {}), "Skipped today").then(loadStatus);
  const backfill = () => act(() => api.post("/discussions/admin/backfill", { days: Number(backfillDays) }), "Backfill queued").then(loadStatus);
  const reportAction = (r, action) => act(() => api.post(`/discussions/admin/report/${r.id}/action`, { action }), "Report handled").then(loadReports);
  const commentAction = (c, action) => act(() => api.post(`/discussions/admin/comment/${c.id}/action`, { action }), `Comment ${action}`).then(loadComments);

  if (authorized === null) return <div className="p-10"><Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-700" /></div>;
  if (authorized === false) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ivory px-4 text-center" data-testid="board-admin-denied">
      <ShieldAlert className="mb-3 h-10 w-10 text-amber-500" />
      <h1 className="font-serif text-2xl font-bold text-emerald-950">Staff only</h1>
      <p className="mt-2 text-slate-600">This area is for Marriage Discussions staff.</p>
      <Link to="/marriage-board"><Button className="mt-4 rounded-full bg-emerald-800 text-white">Back to the board</Button></Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory">
      <header className="sticky top-0 z-20 border-b border-emerald-900/10 bg-ivory/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/marriage-board"><Logo className="h-9" /></Link>
          <span className="text-sm font-semibold text-emerald-900">Discussions Admin</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="font-serif text-3xl font-bold text-emerald-950">Marriage Discussions — Admin</h1>

        {/* Status overview */}
        <div className="mt-5 grid gap-4 rounded-2xl border border-emerald-900/10 bg-white p-5 sm:grid-cols-3" data-testid="board-admin-status">
          <div className="rounded-xl bg-ivory p-3">
            <p className="text-sm font-semibold text-emerald-900">Today</p>
            <p className="mt-1 truncate text-xs text-slate-600" data-testid="admin-today">{status?.today || status?.latestBoardDate || "—"}</p>
          </div>
          <div className="rounded-xl bg-ivory p-3">
            <p className="text-sm font-semibold text-emerald-900">Auto-publish</p>
            <p className="mt-1 text-xs text-slate-600">{status?.paused ? "Paused" : "Active — 1 topic/day"}</p>
          </div>
          <div className="rounded-xl bg-ivory p-3">
            <p className="text-sm font-semibold text-emerald-900">Timezone</p>
            <p className="mt-1 text-xs text-slate-600">{status?.timezone || "America/New_York"}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`rounded-full px-4 py-2 text-sm font-medium ${tab === k ? "bg-emerald-800 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>{label}</button>
          ))}
        </div>

        {/* Topic bank */}
        {tab === "bank" && (
          <div className="mt-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {["pending", "approved", "rejected"].map((s) => (
                <button key={s} onClick={() => setBankStatus(s)} data-testid={`bank-filter-${s}`} className={`rounded-full px-3 py-1.5 text-sm font-medium capitalize ${bankStatus === s ? "bg-emerald-700 text-white" : "bg-white text-emerald-900/70 ring-1 ring-emerald-900/10 hover:bg-mint"}`}>{s}</button>
              ))}
              <Button size="sm" onClick={() => setCreating({ question: "", category: "COMM", intro: "" })} data-testid="admin-create-topic" className="ml-auto rounded-full bg-emerald-800 text-white"><Plus className="mr-1 h-4 w-4" /> New topic</Button>
            </div>

            {creating && (
              <div className="mb-4 space-y-2 rounded-2xl border border-emerald-300 bg-emerald-50/40 p-4" data-testid="admin-create-form">
                <Input value={creating.question} onChange={(e) => setCreating({ ...creating, question: e.target.value })} placeholder="Discussion question" data-testid="create-question" />
                <Input value={creating.category} onChange={(e) => setCreating({ ...creating, category: e.target.value })} placeholder="Category code (e.g. COMM, FAITH, FAMILY…)" data-testid="create-category" />
                <Textarea value={creating.intro} onChange={(e) => setCreating({ ...creating, intro: e.target.value })} placeholder="Optional intro" data-testid="create-intro" />
                <div className="flex gap-2"><Button size="sm" disabled={busy} onClick={createTopic} data-testid="create-save" className="rounded-full bg-emerald-800 text-white">Create</Button><Button size="sm" variant="ghost" onClick={() => setCreating(null)}>Cancel</Button></div>
              </div>
            )}

            <div className="space-y-3" data-testid="admin-bank-list">
              {bank === null ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)
                : bank.length === 0 ? <p className="py-10 text-center text-slate-500">No {bankStatus} topics.</p>
                : bank.map((t) => (
                  <div key={t.id} className="rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`bank-topic-${t.id}`}>
                    {edit?.id === t.id ? (
                      <div className="space-y-2">
                        <Input value={edit.question} onChange={(e) => setEdit({ ...edit, question: e.target.value })} data-testid="admin-edit-question" />
                        <Input value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="admin-edit-category" />
                        <Textarea value={edit.intro || ""} onChange={(e) => setEdit({ ...edit, intro: e.target.value })} data-testid="admin-edit-intro" />
                        <div className="flex gap-2"><Button size="sm" disabled={busy} onClick={saveEdit} data-testid="admin-edit-save" className="rounded-full bg-emerald-800 text-white">Save</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-wide text-amber-600">{t.category || t.categoryCode}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${badge[t.status] || ""}`}>{t.status}</span>
                        </div>
                        <h3 className="mt-1 font-serif text-lg font-semibold text-emerald-900">{t.question || t.title}</h3>
                        {(t.intro) && <p className="mt-1 line-clamp-2 text-sm text-slate-600">{t.intro}</p>}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {t.status !== "approved" && <Button size="sm" disabled={busy} onClick={() => review(t, "approve")} data-testid={`bank-approve-${t.id}`} className="rounded-full bg-emerald-700 text-white hover:bg-emerald-800"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>}
                          {t.status !== "rejected" && <Button size="sm" disabled={busy} variant="outline" onClick={() => review(t, "reject")} data-testid={`bank-reject-${t.id}`} className="rounded-full border-red-300 text-red-600 hover:bg-red-50"><X className="mr-1 h-3.5 w-3.5" /> Reject</Button>}
                          <Button size="sm" variant="outline" onClick={() => setEdit({ id: t.id, question: t.question || t.title, category: t.category || t.categoryCode, intro: t.intro || "" })} data-testid={`bank-edit-${t.id}`} className="rounded-full border-emerald-800/30 text-emerald-900"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                          <Button size="sm" disabled={busy} variant="outline" onClick={() => publishNow(t)} data-testid={`bank-publish-${t.id}`} className="rounded-full border-amber-500/50 text-amber-700 hover:bg-amber-50"><Send className="mr-1 h-3.5 w-3.5" /> Publish now</Button>
                          <Button size="sm" disabled={busy} variant="outline" onClick={() => { const d = prompt("Schedule for date (YYYY-MM-DD):"); if (d) schedule(t, d); }} data-testid={`bank-schedule-${t.id}`} className="rounded-full border-emerald-800/30 text-emerald-900"><CalendarPlus className="mr-1 h-3.5 w-3.5" /> Schedule</Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Comments moderation */}
        {tab === "comments" && (
          <div className="mt-4 space-y-3" data-testid="admin-comments-list">
            {comments === null ? [...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)
              : comments.length === 0 ? <p className="py-10 text-center text-slate-500">No comments awaiting review. 🎉</p>
              : comments.map((c) => (
                <div key={c.id} className="rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`admin-comment-${c.id}`}>
                  <p className="text-xs text-slate-400">{c.author?.communityName || c.authorName || "Member"} · {c.status || "hold"}</p>
                  <p className="mt-1 text-sm text-slate-700">{c.text || c.body}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => commentAction(c, "approve")} data-testid={`comment-approve-${c.id}`} className="rounded-full bg-emerald-700 text-white"><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                    <Button size="sm" disabled={busy} variant="outline" onClick={() => commentAction(c, "delete")} data-testid={`comment-delete-${c.id}`} className="rounded-full border-red-300 text-red-600 hover:bg-red-50"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Reports queue */}
        {tab === "reports" && (
          <div className="mt-4 space-y-3" data-testid="admin-reports-list">
            {reports === null ? [...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)
              : reports.length === 0 ? <p className="py-10 text-center text-slate-500">No open reports. 🎉</p>
              : reports.map((r) => (
                <div key={r.id} className="rounded-2xl border border-emerald-900/10 bg-white p-4" data-testid={`admin-report-${r.id}`}>
                  <p className="text-xs text-slate-400">{r.reason} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : ""}</p>
                  <p className="mt-1 text-sm text-slate-700">{r.comment?.text || r.commentText || r.text || <em className="text-slate-400">[comment unavailable]</em>}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" disabled={busy} onClick={() => reportAction(r, "delete")} data-testid={`report-delete-${r.id}`} className="rounded-full bg-red-600 text-white hover:bg-red-700"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete comment</Button>
                    <Button size="sm" disabled={busy} variant="outline" onClick={() => reportAction(r, "restrict")} data-testid={`report-restrict-${r.id}`} className="rounded-full border-red-300 text-red-600 hover:bg-red-50">Restrict author</Button>
                    <Button size="sm" disabled={busy} variant="outline" onClick={() => reportAction(r, "dismiss")} data-testid={`report-dismiss-${r.id}`} className="rounded-full border-emerald-800/30 text-emerald-900">Dismiss</Button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Publishing controls */}
        {tab === "controls" && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="admin-controls">
            <div className="rounded-2xl border border-emerald-900/10 bg-white p-5">
              <p className="font-semibold text-emerald-900">Auto-publish</p>
              <p className="mt-1 text-sm text-slate-500">One approved topic goes live each day at 08:00 {status?.timezone || "America/New_York"}.</p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => pause(true)} data-testid="admin-pause" variant="outline" className="rounded-full border-amber-500/50 text-amber-700"><Pause className="mr-1 h-3.5 w-3.5" /> Pause</Button>
                <Button size="sm" disabled={busy} onClick={() => pause(false)} data-testid="admin-resume" className="rounded-full bg-emerald-800 text-white"><Play className="mr-1 h-3.5 w-3.5" /> Resume</Button>
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-900/10 bg-white p-5">
              <p className="font-semibold text-emerald-900">Skip / backfill</p>
              <p className="mt-1 text-sm text-slate-500">Skip today's auto-pick, or backfill missing past dates.</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" disabled={busy} onClick={skip} data-testid="admin-skip" variant="outline" className="rounded-full border-emerald-800/30 text-emerald-900"><SkipForward className="mr-1 h-3.5 w-3.5" /> Skip today</Button>
                <Input type="number" value={backfillDays} onChange={(e) => setBackfillDays(e.target.value)} data-testid="admin-backfill-days" className="h-9 w-20 rounded-full" />
                <Button size="sm" disabled={busy} onClick={backfill} data-testid="admin-backfill" variant="outline" className="rounded-full border-emerald-800/30 text-emerald-900"><History className="mr-1 h-3.5 w-3.5" /> Backfill days</Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
